import { getAuthSession } from './authSession'

const TIMER_SNAPSHOT_VERSION = 1
const TIMER_STORAGE_PREFIX = 'akive-activity-timer'
const TIMER_CHANNEL_NAME = 'akive-activity-timer'
const RESTORABLE_STATUSES = new Set(['preparing', 'running', 'paused', 'unsaved', 'saving', 'save-unknown'])
export const ACTIVITY_TIMER_TAB_ID = typeof window !== 'undefined' && window.crypto?.randomUUID
  ? window.crypto.randomUUID()
  : `timer-tab-${Date.now()}-${Math.random().toString(16).slice(2)}`

function getActivityTimerStorageKey(session = getAuthSession()) {
  const sessionKey = session?.clientInstanceId || session?.nickname
  return sessionKey ? `${TIMER_STORAGE_PREFIX}:v${TIMER_SNAPSHOT_VERSION}:${sessionKey}` : null
}

function normalizeTimestamp(value) {
  return Number.isFinite(value) && value >= 0 ? value : null
}

function normalizeTimerState(value) {
  if (!value || !RESTORABLE_STATUSES.has(value.status)) return null

  const startedAt = normalizeTimestamp(value.startedAt)
  const endedAt = normalizeTimestamp(value.endedAt)
  let status = value.status === 'saving' ? 'save-unknown' : value.status

  if (['running', 'paused', 'unsaved', 'save-unknown'].includes(status) && startedAt === null) return null
  if (['unsaved', 'save-unknown'].includes(status) && endedAt === null) status = 'paused'

  return {
    status,
    dailyLogType: typeof value.dailyLogType === 'string' ? value.dailyLogType : 'STUDY',
    contents: typeof value.contents === 'string' ? value.contents : '',
    source: value.source && typeof value.source === 'object' ? value.source : null,
    startedAt,
    endedAt,
    focusAccumulatedMs: Number.isFinite(value.focusAccumulatedMs)
      ? Math.max(0, value.focusAccumulatedMs)
      : 0,
    focusStartedAt: normalizeTimestamp(value.focusStartedAt),
    lastSavedLogOn: typeof value.lastSavedLogOn === 'string' ? value.lastSavedLogOn : null,
    saveVersion: Number.isFinite(value.saveVersion) ? Math.max(0, value.saveVersion) : 0,
  }
}

export function loadActivityTimerSnapshot(session) {
  return loadActivityTimerSnapshotRecord(session)?.state || null
}

export function loadActivityTimerSnapshotRecord(session) {
  const storageKey = getActivityTimerStorageKey(session)
  if (!storageKey || typeof window === 'undefined') return null

  try {
    const snapshot = JSON.parse(window.localStorage.getItem(storageKey))
    if (snapshot?.version !== TIMER_SNAPSHOT_VERSION) return null
    const state = normalizeTimerState(snapshot.state)
    if (!state || typeof snapshot.ownerId !== 'string') return null
    return { state, ownerId: snapshot.ownerId, updatedAt: snapshot.updatedAt || snapshot.savedAt || 0 }
  } catch {
    return null
  }
}

function publishTimerChange(storageKey, snapshot) {
  if (typeof window?.BroadcastChannel !== 'function') return
  try {
    const channel = new window.BroadcastChannel(TIMER_CHANNEL_NAME)
    channel.postMessage({ storageKey, snapshot })
    channel.close()
  } catch {
    // storage 이벤트가 가능한 환경에서는 그 경로로 동기화한다.
  }
}

export function saveActivityTimerSnapshot(state, session, { ownerId = ACTIVITY_TIMER_TAB_ID } = {}) {
  const storageKey = getActivityTimerStorageKey(session)
  if (!storageKey || typeof window === 'undefined') return

  try {
    if (!state || state.status === 'idle') return
    const snapshot = {
      version: TIMER_SNAPSHOT_VERSION,
      savedAt: Date.now(),
      updatedAt: Date.now(),
      ownerId,
      state,
    }
    window.localStorage.setItem(storageKey, JSON.stringify(snapshot))
    publishTimerChange(storageKey, snapshot)
  } catch {
    // 저장소 접근이 제한돼도 현재 탭의 타이머는 계속 동작한다.
  }
}

export function clearActivityTimerSnapshot(
  session,
  { ownerId = ACTIVITY_TIMER_TAB_ID, force = false } = {},
) {
  const storageKey = getActivityTimerStorageKey(session)
  if (!storageKey || typeof window === 'undefined') return
  try {
    const current = loadActivityTimerSnapshotRecord(session)
    if (!force && current && current.ownerId !== ownerId) return
    window.localStorage.removeItem(storageKey)
    publishTimerChange(storageKey, null)
  } catch {
    // 현재 탭 상태 정리는 저장소 접근과 무관하게 계속한다.
  }
}

export function subscribeActivityTimerSnapshot(listener, session) {
  const storageKey = getActivityTimerStorageKey(session)
  if (!storageKey || typeof window === 'undefined') return () => {}

  const handleStorage = (event) => {
    if (event.key !== storageKey) return
    listener(event.newValue ? loadActivityTimerSnapshotRecord(session) : null)
  }
  window.addEventListener('storage', handleStorage)

  let channel
  if (typeof window.BroadcastChannel === 'function') {
    try {
      channel = new window.BroadcastChannel(TIMER_CHANNEL_NAME)
      channel.addEventListener('message', ({ data }) => {
        if (data?.storageKey === storageKey) listener(data.snapshot
          ? { ...data.snapshot, state: normalizeTimerState(data.snapshot.state) }
          : null)
      })
    } catch {
      channel = null
    }
  }

  return () => {
    window.removeEventListener('storage', handleStorage)
    channel?.close()
  }
}
